import { useCallback } from 'react';
import { getPaidyConfigApi } from '@/api/payment';
import { store } from '@/config/store';

type PaidyMode = 'token' | 'payment';

interface PaidyCheckoutParams {
  mode: PaidyMode;
  amount?: number;
  orderRef?: string;
  storeName?: string;
}

interface PaidyCheckoutResult {
  success: boolean;
  paymentId?: string;
  tokenId?: string;
}

export const usePaidy = () => {
  const launchPaidyCheckout = useCallback(
    async (params: PaidyCheckoutParams): Promise<PaidyCheckoutResult> => {
      const { mode, amount, orderRef, storeName } = params;

      // Paidy公開鍵を取得（modeに応じたアカウントのキーを取得）
      const { data: configData } = await getPaidyConfigApi(mode);
      if (!configData.success) {
        throw new Error('Paidy設定情報の取得に失敗しました');
      }

      const { public_key, logo_url, buyer_data } = configData.data;

      if (!window.Paidy) {
        throw new Error('Paidy Checkout JSが読み込まれていません');
      }

      return new Promise<PaidyCheckoutResult>((resolve) => {
        const config: PaidyConfigPayload = {
          api_key: public_key,
          logo_url: logo_url || undefined,
          closed: (callbackData: PaidyCallbackData) => {
            console.log('Paidy closed callback:', JSON.stringify(callbackData));

            if (mode === 'token') {
              // トークンモード: idがtok_で始まるか、token_idがあれば成功
              const tokenId =
                callbackData.token_id ||
                (callbackData.id?.startsWith('tok_') ? callbackData.id : null);
              if (tokenId) {
                resolve({
                  success: true,
                  tokenId: tokenId,
                });
              } else {
                resolve({ success: false });
              }
            } else {
              // 決済モード: idがあれば成功
              if (callbackData.id) {
                resolve({
                  success: true,
                  paymentId: callbackData.id,
                });
              } else {
                resolve({ success: false });
              }
            }
          },
        };

        // トークンモードの場合はtoken設定を追加
        if (mode === 'token') {
          config.token = {
            wallet_id: 'default',
            type: 'recurring',
          };
        }

        const paidyHandler = window.Paidy.configure(config);

        // 顧客情報取得
        const customerInfo = store.state.customerInfo;

        const launchPayload: PaidyLaunchPayload = {
          amount: mode === 'payment' ? amount : undefined,
          currency: 'JPY',
          store_name: 'マイくる',
          buyer: {
            email: customerInfo?.email || undefined,
            name1: customerInfo?.family_name
              ? `${customerInfo.family_name} ${customerInfo.first_name || ''}`
              : undefined,
            phone: customerInfo?.phone_no1 || undefined,
          },
          buyer_data: buyer_data || undefined,
          order:
            mode === 'payment'
              ? {
                  order_ref: orderRef || `order_${Date.now()}`,
                  items: [
                    {
                      title: 'プリペイドチャージ',
                      quantity: 1,
                      unit_price: amount || 0,
                    },
                  ],
                }
              : undefined,
        };

        paidyHandler.launch(launchPayload);
      });
    },
    [],
  );

  return { launchPaidyCheckout };
};
